/* eslint-disable functional/prefer-readonly-type */
import React, { useEffect } from 'react'
import { useLang } from '~/hooks/useLang'
import {
  Button,
  Card,
  CardHeader,
  Collapse,
  Grid,
  IconButton,
} from '@mui/material'
import { ErrorBoundary } from 'react-error-boundary'
import Layout from '~/components/Layout'
import ErrorFallback from '~/components/ErrorFallback'
import useReportState from '../../stores/ReportState'
import {
  downloadReportCSV,
  makeRequestToGetReportTypesOccurrencesDetailed,
} from '../../services/report'
import notify from '~/utils/notify'
import { useHookstate } from '@hookstate/core'
import FilterAutocompleteDispatchsGroups from '~/components/FilterAutocompleteDispatchsGroups'
import FilterSelectPeriod from '~/components/FilterAutocompletePeriod'
import FilterAutocompleteAgencies from '~/components/FilterAutocompleteAgencies'
import FilterSwitch from './SpecificFilters/FilterSwitchGroupLocation'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import FilterAutocompleteCategory from '~/components/FilterAutocompleteCategory'
import GenerateReportButton from '../../components/GenerateReportButton'
import FilterSwitchOtherDispatchsGroup from '~/components/FilterSwitchOtherDispatchsGroups'
import ExportButtonCSV from '../../components/ButtonExportCSV'
import FilterAutocompleteCity from '~/components/FilterSelectAutocompleteCity'
import FilterAutocompleteEntryOrigin from '~/components/FilterAutocompleteEntryOrigin'
import FilterAutocompleteClosedStatus from '~/components/FilterAutocompleteClosedStatus'
import FilterAutocompleteDispatchGroupSet from '~/components/FilterAutocompleteDispatchGroupSet'
import FilterAutocompleteTypesAndSubtypesWithDescription, {
  type typesAndSubtypesSelectedProps,
} from '~/components/FilterAutocompleteTypesAndSubtypesWithDescription'
import { formatTypeAndSubtypeForReport } from '~/utils/format'
import FilterAutocompleteOperation from '~/components/FilterAutocompleteOperation'
import FilterTextField from '~/components/FilterTextField'
import FilterAutocompleteDistrict from '~/components/FilterSelectAutocompleteDistrict'
import { generateCSVFileName } from '~/utils/strings'

// eslint-disable-next-line max-lines-per-function
export default function TypesOccurrencesDetailed() {
  const { translate } = useLang()
  const isLoading = useHookstate(false)
  const expandFilterSearch = useHookstate<boolean>(true)
  const deviceId = useHookstate<number | null>(null)
  const lockFields = useHookstate<boolean>(false)
  const groupId = useHookstate<readonly number[]>([])
  const categoryId = useHookstate<number | null>(null)
  const startDate = useHookstate<string | null>(null)
  const finishDate = useHookstate<string | null>(null)
  const agencyIds = useHookstate<readonly number[]>([])
  const labelButton = useHookstate('')
  const period = useHookstate('')
  const locationGrouping = useHookstate<boolean>(false)
  const simpleReport = useHookstate<boolean>(false)
  const withSolvedThirdParty = useHookstate<boolean>(false)
  const cityId = useHookstate<number | null>(null)
  const entryOriginId = useHookstate<number | null>(null)
  const closedStatusId = useHookstate<number | null>(null)
  const groupSetId = useHookstate<number | null>(null)
  const typeId = useHookstate<number | null>(null)
  const subtypeId = useHookstate<number[] | null>(null)
  const typesAndSubtypesSelected = useHookstate<
    typesAndSubtypesSelectedProps[] | []
  >([])
  const typesFormatedForSending = useHookstate<number[]>([])
  const subTypesFormatedForSending = useHookstate<number[]>([])
  const operationId = useHookstate<number | null>(null)
  const location = useHookstate<string | null>(null)
  const districtId = useHookstate<number | null>(null)

  const { reportData, loadingReport } = useReportState()

  useEffect(() => {
    document.title = translate('Reports - Dispatch System')
  }, [])

  async function generateReport(exportCSV = false) {
    if (startDate && finishDate) {
      formatTypeAndSubtypeForReport({
        typesAndSubtypesSelected: typesAndSubtypesSelected.get(),
        typesFormatedForSending: typesFormatedForSending,
        subTypesFormatedForSending: subTypesFormatedForSending,
      })
      isLoading.set(true)
      loadingReport().set(true)

      const data = await makeRequestToGetReportTypesOccurrencesDetailed({
        startDate: startDate.get(),
        finishDate: finishDate.get(),
        typeIds: typesFormatedForSending.get(),
        subtypeIds: subTypesFormatedForSending.get(),
        dispatchGroupsId: groupId.get(),
        groupByLocation: locationGrouping.get(),
        simpleReport: simpleReport.get(),
        agencyIds: agencyIds.get(),
        categoryId: categoryId.get(),
        withSolvedThirdParty: withSolvedThirdParty.get(),
        cityId: cityId.get(),
        entryOriginId: entryOriginId.get(),
        closedStatusId: closedStatusId.get(),
        groupSetId: groupSetId.get(),
        exportCSV: exportCSV,
        operationId: operationId.get(),
        location: location.get(),
        districtId: districtId.get(),
      }).finally(() => {
        loadingReport().set(false)
        isLoading.set(false)
        typesFormatedForSending.set([])
        subTypesFormatedForSending.set([])
      })
      if (!data || (Array.isArray(data) && data.length === 0)) {
        notify({
          message: translate('Não há dados suficientes para gerar o relatório'),
          type: 'error',
        })
        return
      }

      if (exportCSV) {
        downloadReportCSV(
          generateCSVFileName('Occurrences by dispatch group and type', translate),
          data
        )
      } else {
        reportData().set(data)
        window.open(`/report/result/types-occurrences-detailed`)
      }
    } else {
      notify({
        message: translate('Initial and final date are mandatory'),
        type: 'error',
      })
    }
  }

  const handleClearFilters = () => {
    categoryId.set(null)
    deviceId.set(null)
    agencyIds.set([])
    groupSetId.set(null)
    groupId.set([])
    closedStatusId.set(null)
    entryOriginId.set(null)
    cityId.set(null)
    period.set('')
    typeId.set(null)
    subtypeId.set([])
    withSolvedThirdParty.set(false)
    simpleReport.set(false)
    locationGrouping.set(false)
    operationId.set(null)
    location.set(null)
    districtId.set(null)
  }

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <Layout marginLayout={true}>
        <Card sx={{ boxShadow: 4, marginBottom: 2 }}>
          <Grid container xs={12}>
            <Grid item>
              <CardHeader
                title={`${translate('Report')}: ${translate(
                  `Occurrences by dispatch group and type`
                )}`}
              />
            </Grid>
          </Grid>
        </Card>
        <Card sx={{ boxShadow: 4, marginBottom: 2 }}>
          <CardHeader
            title={`${translate('Filters')}`}
            avatar={
              <IconButton
                onClick={() =>
                  expandFilterSearch.set(!expandFilterSearch.get())
                }
                sx={{
                  display: 'flex',
                  justifyContent: 'end',
                  marginRight: 1,
                  padding: 0,
                }}
              >
                {expandFilterSearch.value ? (
                  <ExpandLessIcon />
                ) : (
                  <ExpandMoreIcon />
                )}
              </IconButton>
            }
          />

          <Collapse in={expandFilterSearch.get()}>
            <Grid
              container
              xs={12}
              sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                padding: 2,
              }}
            >
              <Grid container direction={'row'} rowGap={2}>
                <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
                  <FilterAutocompleteCategory
                    disabled={lockFields.get()}
                    categoryId={categoryId.get()}
                    onCategoryChange={(category) => {
                      categoryId.set(category)
                      typeId.set(null)
                    }}
                  />
                </Grid>
                <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
                  <FilterAutocompleteAgencies
                    disabled={lockFields.get()}
                    agenciesId={agencyIds.get()}
                    onAgenciesChange={(agency) => {
                      labelButton.set(translate('Search'))
                      agencyIds.set(agency)
                    }}
                  />
                </Grid>
                <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
                  <FilterAutocompleteDispatchGroupSet
                    disabled={lockFields.get()}
                    dispatchGroupSetId={groupSetId?.get() ?? undefined}
                    onGroupDispatchSetChange={(group) => {
                      groupSetId?.set(group)
                    }}
                  />
                </Grid>
                <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
                  <FilterAutocompleteDispatchsGroups
                    disabled={lockFields.get()}
                    agencyId={agencyIds.get() ?? undefined}
                    dispatcheGroups={groupId.get() ?? undefined}
                    onGroupDispatchChange={(group) => {
                      groupId.set(group)
                      deviceId.set(null)
                      labelButton.set(translate('Search'))
                    }}
                    groupSetId={groupSetId.get()}
                  />
                </Grid>
                <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
                  <FilterAutocompleteEntryOrigin
                    disabled={lockFields.get()}
                    onEntryOriginChange={(entryOrigin) => {
                      entryOriginId?.set(entryOrigin)
                      labelButton.set(translate('Search'))
                    }}
                    originId={entryOriginId?.get()}
                  />
                </Grid>
                <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
                  <FilterAutocompleteCity
                    disabled={lockFields.get()}
                    onCityChange={(city) => {
                      cityId?.set(city)
                      labelButton.set(translate('Search'))
                    }}
                    cityId={cityId?.get()}
                  />
                </Grid>
                <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
                  <FilterAutocompleteDistrict
                    disabled={!cityId?.get()}
                    onDistrictChange={(district) => {
                      districtId?.set(district)
                    }}
                    districtId={districtId}
                    cityId={cityId ? cityId.get() : null}
                  />
                </Grid>
                <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
                  <FilterTextField
                    currentText={location.get()}
                    label={'Location'}
                    onTextFieldChange={(textField) => {
                      location.set(textField)
                    }}
                  />
                </Grid>
                <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
                  <FilterAutocompleteClosedStatus
                    disabled={lockFields.get()}
                    closedStatusId={closedStatusId?.get()}
                    onClosedStatusIdChange={(closedStatus) => {
                      closedStatusId?.set(closedStatus)
                      labelButton.set(translate('Search'))
                    }}
                  />
                </Grid>
                <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
                  <FilterAutocompleteOperation
                    disabled={lockFields.get()}
                    onOperationChange={(operation) => {
                      operationId.set(operation)
                      labelButton.set(translate('Search'))
                    }}
                    operationId={operationId.get()}
                  />
                </Grid>
                <Grid container xs={12}>
                  <FilterSelectPeriod
                    disabled={lockFields.get()}
                    onStartDateChange={(value) =>
                      startDate.set(value?.toJSON() ?? null)
                    }
                    onFinishDateChange={(value) =>
                      finishDate.set(value?.toJSON() ?? null)
                    }
                    onPeriodChange={(value) => {
                      period.set(value)
                    }}
                    period={period.get()}
                    required
                  />
                </Grid>

                <Grid container xs={12} direction={'row'}>
                  <Grid item xs={4} sx={{ pl: 2, pt: 1 }}>
                    <FilterSwitchOtherDispatchsGroup
                      onCheckedChange={() => {
                        labelButton.set(translate('Search'))
                        withSolvedThirdParty.set(!withSolvedThirdParty.value)
                      }}
                      disabled={lockFields.get()}
                      checked={withSolvedThirdParty.get()}
                    />
                  </Grid>

                  <Grid item xs={2} sx={{ pl: 2, pt: 1 }}>
                    <FilterSwitch
                      label={translate('Group by city')}
                      onCheckedChange={() => {
                        locationGrouping.set(!locationGrouping.value)
                      }}
                      disabled={lockFields.get()}
                      checked={locationGrouping.get()}
                    />
                  </Grid>

                  <Grid item xs={2} sx={{ pl: 2, pt: 1 }}>
                    <FilterSwitch
                      label={translate('Simple report')}
                      onCheckedChange={() => {
                        simpleReport.set(!simpleReport.value)
                      }}
                      disabled={lockFields.get()}
                      checked={simpleReport.get()}
                    />
                  </Grid>
                </Grid>

                <Grid item xs={9} sx={{ pl: 1, pt: 1 }}>
                  <FilterAutocompleteTypesAndSubtypesWithDescription
                    typeId={typeId}
                    subtypeIds={subtypeId}
                    lockFields={false}
                    typesAndSubtypesSelected={typesAndSubtypesSelected}
                    category={categoryId.get() ?? null}
                  />
                </Grid>
              </Grid>
              <Grid
                item
                xs={12}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  gap: 2,
                  pt: 2,
                }}
              >
                <Button
                  variant="outlined"
                  onClick={() => handleClearFilters()}
                  sx={{ height: '40px' }}
                >
                  {translate('Clear Filters')}
                </Button>
                <ExportButtonCSV
                  isLoading={isLoading.get()}
                  dataToExport={() => generateReport(true)}
                />
                <GenerateReportButton
                  isLoading={isLoading.get()}
                  makeReport={generateReport}
                />
              </Grid>
            </Grid>
          </Collapse>
        </Card>
      </Layout>
    </ErrorBoundary>
  )
}
